import { supabase } from '../config/supabase';

/**
 * Crea una nueva orden de compra
 * @param {Object} orden - Datos de la orden
 * @returns {Promise<Object>} Orden creada
 */
export const crearOrden = async (orden) => {
  try {
    const { data, error } = await supabase
      .from('ordenes_compra')
      .insert([{
        user_id: orden.user_id,
        producto_id: orden.producto_id,
        monto: orden.monto,
        cantidad: orden.cantidad || 1, // Por defecto 1 si no se especifica
        mp_preference_id: orden.mp_preference_id,
        external_reference: orden.external_reference,
        estado: 'pendiente'
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error al crear orden:', error);
    throw error;
  }
};

/**
 * Actualiza el estado de una orden
 * @param {string} id - ID de la orden
 * @param {string} estado - Nuevo estado
 * @param {Object} metadata - Datos adicionales
 * @returns {Promise<Object>} Orden actualizada
 */
export const actualizarEstadoOrden = async (id, estado, metadata = {}) => {
  try {
    const { data, error } = await supabase
      .from('ordenes_compra')
      .update({
        estado,
        mp_status: metadata.mp_status,
        mp_payment_id: metadata.mp_payment_id,
        metadata,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error al actualizar orden:', error);
    throw error;
  }
};

/**
 * Obtiene las órdenes de un usuario
 * @param {string} user_id - ID del usuario
 * @returns {Promise<Array>} Lista de órdenes
 */
export const obtenerOrdenesUsuario = async (user_id) => {
  try {
    const { data, error } = await supabase
      .from('ordenes_compra')
      .select(`
        *,
        productos (
          id,
          nombre,
          precio,
          imagen_url
        )
      `)
      .eq('user_id', user_id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error al obtener órdenes:', error);
    throw error;
  }
};

/**
 * Obtiene todas las órdenes con paginación y filtrado
 * @param {Object} options - Opciones de consulta
 * @param {number} options.limit - Límite de resultados
 * @param {number} options.offset - Offset para paginación
 * @param {string} options.searchText - Texto para buscar
 * @param {string} options.estado - Filtro por estado
 * @param {string} options.orderBy - Campo de ordenamiento
 * @returns {Promise<Object>} Resultados con paginación
 */
export const obtenerOrdenesPaginadas = async (options = {}) => {
  try {
    const {
      limit = 50,
      offset = 0,
      searchText = null,
      estado = null,
      orderBy = 'created_at'
    } = options;

    let query = supabase
      .from('ordenes_compra')
      .select(`
        *,
        productos (
          id,
          nombre,
          precio,
          imagen_url
        )
      `, { count: 'exact' });

    // Aplicar filtro de estado si existe
    if (estado) {
      query = query.eq('estado', estado);
    }

    // Aplicar ordenamiento
    const orderDirection = orderBy.startsWith('-') ? { ascending: false } : { ascending: true };
    const orderField = orderBy.startsWith('-') ? orderBy.substring(1) : orderBy;
    query = query.order(orderField, orderDirection);

    // Aplicar paginación
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) throw error;

    // Filtrar por texto de búsqueda en el cliente si existe
    let filteredData = data;
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filteredData = data.filter(order => {
        const user = order.user_id || '';
        const product = order.productos?.nombre || '';
        return user.toLowerCase().includes(searchLower) || 
               product.toLowerCase().includes(searchLower);
      });
    }

    return {
      orders: filteredData,
      totalCount: count || 0,
      hasMore: (offset + limit) < (count || 0),
      nextOffset: (offset + limit) < (count || 0) ? offset + limit : null
    };
  } catch (error) {
    console.error('Error al obtener órdenes paginadas:', error);
    throw error;
  }
};

/**
 * Obtiene una orden por external_reference
 * @param {string} external_reference - Referencia externa
 * @returns {Promise<Object>} Orden
 */
export const obtenerOrdenPorReferencia = async (external_reference) => {
  try {
    const { data, error } = await supabase
      .from('ordenes_compra')
      .select('*')
      .eq('external_reference', external_reference)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error al obtener orden por referencia:', error);
    throw error;
  }
};
